"use client";

import React from "react";
import { useLocalizedText } from "@/content/i18n/context";

export interface ServiceSectionProps {
  title: string;
  children: React.ReactNode;
  className?: string;
  titleLevel?: "h1" | "h2" | "h3";
  spacing?: "default" | "large" | "small";
  id?: string;
  /** Uppercase tracked label rendered above the title. */
  eyebrow?: string;
  /** Short supporting line rendered under the title. */
  dek?: string;
  /** Substring of the title that receives the editorial underline. */
  accent?: string;
  /** Renders the section as a full-bleed band with vertical rhythm. */
  band?: "ivory" | "surface";
}

export default function ServiceSection({
  title,
  children,
  className = "",
  titleLevel = "h2",
  spacing = "default",
  id,
  eyebrow,
  dek,
  accent,
  band,
}: ServiceSectionProps) {
  const translatedTitle = useLocalizedText(title);
  const translatedEyebrow = useLocalizedText(eyebrow ?? "");
  const translatedDek = useLocalizedText(dek ?? "");
  const translatedAccent = useLocalizedText(accent ?? "");

  const getSpacingClass = () => {
    switch (spacing) {
      case "large":
        return "mb-20";
      case "small":
        return "mb-12";
      default:
        return "mb-16";
    }
  };

  const getTitleClass = () => {
    switch (titleLevel) {
      case "h1":
        return "font-display text-3xl md:text-4xl font-bold text-neutral-emphasis mb-6";
      case "h3":
        return "font-display text-xl font-bold text-neutral-emphasis mb-4";
      default:
        return "font-display text-3xl md:text-4xl font-bold text-neutral-emphasis mb-6";
    }
  };

  const TitleTag = titleLevel;

  // Wrap the matching title substring in the editorial underline span.
  const renderTitle = () => {
    if (!translatedAccent) {
      return translatedTitle;
    }
    const matchIndex = translatedTitle.indexOf(translatedAccent);
    if (matchIndex === -1) {
      return translatedTitle;
    }
    const before = translatedTitle.slice(0, matchIndex);
    const after = translatedTitle.slice(matchIndex + translatedAccent.length);
    return (
      <>
        {before}
        <span className="emphasis-underline">{translatedAccent}</span>
        {after}
      </>
    );
  };

  const header = (
    <>
      {eyebrow ? (
        <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-emphasis/50">
          {translatedEyebrow}
        </p>
      ) : null}
      <TitleTag className={getTitleClass()}>{renderTitle()}</TitleTag>
      {dek ? (
        <p className="-mt-2 mb-8 max-w-2xl text-lg leading-relaxed text-neutral-muted">
          {translatedDek}
        </p>
      ) : null}
    </>
  );

  if (band) {
    const bandClass = band === "surface" ? "bg-neutral-surface" : "bg-background";
    return (
      <section id={id} className={`${bandClass} py-24 lg:py-32 ${className}`}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {header}
          {children}
        </div>
      </section>
    );
  }

  return (
    <div id={id} className={`${getSpacingClass()} ${className}`}>
      {header}
      {children}
    </div>
  );
}
